<template>
	<div class="container mt-5">
		<div class="row justify-content-center">
			<div class="col-md-8 col-lg-6">
				<!-- Chargement -->
				<div v-if="loading" class="text-center py-5">
					<b-spinner variant="primary" label="Loading..."></b-spinner>
					<p class="mt-3">{{ $t('monte.payment_result_loading') }}</p>
				</div>

				<!-- === MODE CONTRAT === -->
				<template v-else-if="paymentType === 'contract'">

					<!-- Signature Yousign requise après paiement -->
					<PaymentStep
						v-if="status === 'signing'"
						:amount="0"
						:phase="yousignPhase"
						:signature-url="yousignUrl"
						:invoice="yousignInvoice"
						:checking-signed="checkingSignedManual"
						:downloading-contract="downloadingSignedContract"
						:contract-id="parsedIds[0]"
						@check-signed="onManualCheckSigned"
						@download-contract="onDownloadSignedContract"
						@signature-confirmed="onSignatureConfirmed"
					/>

					<!-- Succès : réutilisation de ConfirmationStep -->
					<ConfirmationStep
						v-else-if="status === 'success'"
						:contract-id="parsedIds[0]"
					>
						<template #actions>
							<div class="d-flex flex-column align-items-center">
								<b-button variant="primary" @click="goToNewContract">
									<font-awesome-icon :icon="['fal', 'plus']" class="mr-1" />
									{{ $t('monte.creer_nouveau_contrat') }}
								</b-button>
								<b-button variant="outline-secondary" class="mt-2" @click="goToContracts">
									{{ $t('monte.payment_result_go_contracts') }}
								</b-button>
							</div>
						</template>
					</ConfirmationStep>

					<!-- En attente -->
					<div v-else-if="status === 'pending'" class="text-center py-5">
						<font-awesome-icon :icon="['fal', 'clock']" class="text-warning mb-3" style="font-size: 4rem;" />
						<h3>{{ $t('monte.payment_result_pending_title') }}</h3>
						<p class="text-muted">{{ $t('monte.payment_result_pending_desc') }}</p>
						<p class="small text-muted">{{ $t('monte.payment_result_pending_retry') }}</p>
						<div class="mt-3 d-flex flex-column align-items-center">
							<b-button variant="primary" @click="goToContracts">
								{{ $t('monte.payment_result_go_contracts') }}
							</b-button>
							<b-button variant="outline-secondary" class="mt-2" :disabled="signingLater" @click="onPayLater">
								<b-spinner v-if="signingLater" small class="mr-1"></b-spinner>
								{{ $t('monte.payment_result_pay_later') }}
							</b-button>
						</div>
					</div>

					<!-- Erreur -->
					<div v-else class="text-center py-5">
						<font-awesome-icon :icon="['fal', 'times-circle']" class="text-danger mb-3" style="font-size: 4rem;" />
						<h3>{{ $t('monte.payment_result_error_title') }}</h3>
						<p class="text-muted">{{ $t('monte.payment_result_error_desc') }}</p>
						<div class="mt-3 d-flex flex-column align-items-center">
							<b-button variant="primary" :disabled="retrying" @click="retryPayment">
								<b-spinner v-if="retrying" small class="mr-1"></b-spinner>
								<font-awesome-icon v-else :icon="['fal', 'redo']" class="mr-1" />
								{{ $t('monte.payment_result_retry') }}
							</b-button>
							<b-button variant="outline-secondary" class="mt-2" :disabled="signingLater" @click="onPayLater">
								<b-spinner v-if="signingLater" small class="mr-1"></b-spinner>
								{{ $t('monte.payment_result_pay_later') }}
							</b-button>
							<b-button variant="link" class="mt-2" @click="goToContracts">
								{{ $t('monte.payment_result_go_contracts') }}
							</b-button>
						</div>
					</div>
				</template>

				<!-- === MODE FACTURE (duedates / details) === -->
				<template v-else>
					<!-- Succès facture -->
					<div v-if="status === 'success'" class="signature-step">
						<!-- Confetti -->
						<div v-if="showFireworks" class="fireworks-container">
							<div v-for="n in 50" :key="n" class="confetti" :style="confettiStyle(n)" />
						</div>

						<div class="text-center mb-4">
							<div class="sig-confirmation-icon bg-primary text-white mb-2">
								<font-awesome-icon :icon="['fal', 'check-circle']" />
							</div>
							<h5 class="font-weight-bold mb-0">{{ $t('monte.payment_result_invoice_success_title') }}</h5>
							<p class="text-muted mt-2">{{ $t('monte.payment_result_invoice_success_desc') }}</p>
						</div>

						<!-- Liste des factures payées -->
						<div v-if="paidInvoices.length > 0">
							<div v-for="invoice in paidInvoices" :key="invoice.invoice_id" class="sig-step-row mb-3">
								<div class="sig-step-badge bg-primary text-white">
									<font-awesome-icon :icon="['fal', 'file-invoice-dollar']" />
									<font-awesome-icon :icon="['fal', 'check-circle']" class="sig-step-check text-primary" />
								</div>
								<div class="ml-3 flex-grow-1">
									<p class="font-weight-bold mb-1">{{ invoice.invoice_num || invoice.invoice_date }}</p>
									<button
										type="button"
										class="btn btn-link p-0"
										:disabled="downloadingInvoiceId === invoice.invoice_id"
										@click.prevent="downloadInvoicePdf(invoice)"
									>
										<b-spinner v-if="downloadingInvoiceId === invoice.invoice_id" small class="mr-1" />
										<font-awesome-icon v-else :icon="['fal', 'download']" class="mr-1" />
										{{ $t('monte.payment_result_download_invoice') }}
									</button>
								</div>
							</div>
						</div>

						<div class="text-center mt-4">
							<b-button variant="primary" @click="goToInvoices">
								{{ $t('monte.payment_result_go_invoices') }}
							</b-button>
						</div>
					</div>

					<!-- En attente facture -->
					<div v-else-if="status === 'pending'" class="text-center py-5">
						<font-awesome-icon :icon="['fal', 'clock']" class="text-warning mb-3" style="font-size: 4rem;" />
						<h3>{{ $t('monte.payment_result_pending_title') }}</h3>
						<p class="text-muted">{{ $t('monte.payment_result_pending_desc') }}</p>
						<p class="small text-muted">{{ $t('monte.payment_result_pending_retry') }}</p>
						<div class="mt-3">
							<b-button variant="primary" @click="goToInvoices">
								{{ $t('monte.payment_result_go_invoices') }}
							</b-button>
						</div>
					</div>

					<!-- Erreur facture -->
					<div v-else class="text-center py-5">
						<font-awesome-icon :icon="['fal', 'times-circle']" class="text-danger mb-3" style="font-size: 4rem;" />
						<h3>{{ $t('monte.payment_result_error_title') }}</h3>
						<p class="text-muted">{{ $t('monte.payment_result_invoice_error_desc') }}</p>
						<div class="mt-3 d-flex flex-column align-items-center">
							<b-button variant="primary" :disabled="retrying" @click="retryInvoicePayment">
								<b-spinner v-if="retrying" small class="mr-1"></b-spinner>
								<font-awesome-icon v-else :icon="['fal', 'redo']" class="mr-1" />
								{{ $t('monte.payment_result_retry') }}
							</b-button>
							<b-button variant="link" class="mt-2" @click="goToInvoices">
								{{ $t('monte.payment_result_go_invoices') }}
							</b-button>
						</div>
					</div>
				</template>
			</div>
		</div>

		<!-- Formulaire caché pour redirection vers Paybox -->
		<form v-if="go_paybox" method="POST" :action="paybox_infos.url" id="redirectFormPayboxRetry" class="d-none">
			<template v-for="(value, key) in paybox_infos">
				<input v-if="key !== 'url'" :key="key" type="hidden" :name="key" :value="value">
			</template>
			<button id="button_go_paybox_retry" type="submit">{{ $t('global.valider') }}</button>
		</form>

		<!-- Formulaire caché Sogecommerce -->
		<form v-if="go_sogecommerce" method="POST" :action="sogecommerce_infos.url" id="redirectFormSogecommerceRetry" class="d-none">
			<template v-for="(value, key) in sogecommerce_infos">
				<input v-if="key !== 'url'" type="hidden" :name="key" :value="value" :key="key">
			</template>
			<button id="button_go_sogecommerce_retry" type="submit">{{ $t('global.valider') }}</button>
		</form>
	</div>
</template>

<script>
import ContractMixin from "@/mixins/Contract.js"
import InvoiceMixin from "@/mixins/Invoice.js"
import Marketplace from "@/mixins/Marketplace.js"
import ConfirmationStep from "@/components/Contract/ConfirmationStep"
import PaymentStep from "@/components/Contract/PaymentStep"

export default {
	name: 'PaymentResult',
	mixins: [ContractMixin, InvoiceMixin, Marketplace],
	components: { ConfirmationStep, PaymentStep },
	data() {
		return {
			loading: true,
			status: null,
			pollCount: 0,
			pollTimer: null,
			maxPolls: 10,
			signingLater: false,
			paidInvoices: [],
			downloadingInvoiceId: null,
			retrying: false,
			showFireworks: false,
			// Formulaires cachés marketplace
			marketplace_type: null,
			go_paybox: false,
			go_sogecommerce: false,
			paybox_infos: {},
			sogecommerce_infos: {},
			// Yousign signature flow
			yousignPhase: 'signing',
			yousignUrl: null,
			yousignInvoice: null,
			checkingSignedManual: false,
			downloadingSignedContract: false,
		}
	},
	computed: {
		/**
		 * Détermine le type de paiement depuis le paramètre de route.
		 * @returns {string} 'contract' | 'duedates' | 'details'
		 */
		paymentType() {
			const type = this.$route.params.type
			if (type === 'duedates' || type === 'details') return type
			return 'contract'
		},
		/**
		 * Parse les identifiants depuis les paramètres de route ou query.
		 * @returns {Array<number>}
		 */
		parsedIds() {
			// 1. Route param (path: /payment-result/:type/:ids)
			let ids = this.$route.params.ids
			// 2. Fallback: ancien format sans type (rétrocompat)
			if (!ids) ids = this.$route.params.type
			// 3. Fallback: query param
			if (!ids) ids = this.$route.query.ids || this.$route.query.contract_ids
			// 4. Fallback: query param avant le # (ajouté par CAWL)
			if (!ids) {
				const searchParams = new URLSearchParams(window.location.search)
				ids = searchParams.get('ids') || searchParams.get('contract_ids')
			}
			if (!ids) return []
			return String(ids).split(',').map(Number).filter(n => !isNaN(n) && n > 0)
		},
	},
	async mounted() {
		// Nettoyer les query params CAWL (?RETURNMAC=...&hostedCheckoutId=...) de l'URL
		if (window.location.search) {
			window.history.replaceState(null, '', window.location.pathname + window.location.hash)
		}

		if (this.parsedIds.length === 0) {
			this.status = 'error'
			this.loading = false
			return
		}
		await this.checkStatus()
	},
	beforeDestroy() {
		if (this.pollTimer) clearTimeout(this.pollTimer)
	},
	methods: {
		/**
		 * Vérifie le statut du paiement avec polling automatique.
		 * Redirige vers le bon endpoint selon le type (contrat ou facture).
		 */
		async checkStatus() {
			this.loading = true

			let result
			if (this.paymentType === 'contract') {
				result = await this.checkPaymentResult(this.parsedIds)
			} else {
				result = await this.checkMarketplacePaymentResult(this.paymentType, this.parsedIds)
			}

			if (!result) {
				this.status = 'error'
				this.loading = false
				return
			}

			this.status = result.status

			// Paiement contrat réussi mais signature Yousign requise
			if (result.status === 'success' && this.paymentType === 'contract' && result.signature_required) {
				await this.initYousignFlow()
				this.status = 'signing'
				this.loading = false
				return
			}

			if (result.status === 'success' && this.paymentType !== 'contract') {
				this.showFireworks = true
				setTimeout(() => { this.showFireworks = false }, 8000)

				// Charger les factures payées
				if (result.invoice_ids && result.invoice_ids.length > 0) {
					await this.loadPaidInvoices(result.invoice_ids)
				}
			}

			if (result.status === 'pending' && this.pollCount < this.maxPolls) {
				this.pollCount++
				this.pollTimer = setTimeout(() => this.checkStatus(), 5000)
				this.loading = false
				return
			}

			// Après max polls sans réponse définitive, considérer comme erreur
			if (result.status === 'pending') {
				this.status = 'error'
			}

			this.loading = false
		},
		/**
		 * Charge les factures payées pour affichage dans le récapitulatif.
		 * @param {Array<number>} invoiceIds - Identifiants des factures.
		 */
		async loadPaidInvoices(invoiceIds) {
			try {
				const invoicesToPay = await this.getAddressedInvoicesToPay()
				const invoicesPaid = await this.getAddressedInvoicesSold()
				const allInvoices = [...(invoicesToPay || []), ...(invoicesPaid || [])]
				this.paidInvoices = allInvoices.filter(inv => invoiceIds.includes(inv.invoice_id))
			} catch (e) {
				console.error('PaymentResult::loadPaidInvoices => ERROR', e)
			}
		},
		/**
		 * Télécharge le PDF d'une facture payée.
		 * @param {Object} invoice - Objet facture.
		 */
		async downloadInvoicePdf(invoice) {
			this.downloadingInvoiceId = invoice.invoice_id
			try {
				await this.getAddressedPdfInvoice([invoice])
			} catch (e) {
				console.error('PaymentResult::downloadInvoicePdf => ERROR', e)
			} finally {
				this.downloadingInvoiceId = null
			}
		},
		/**
		 * Signe le contrat sans paiement (payer plus tard par virement).
		 */
		async onPayLater() {
			if (this.parsedIds.length === 0) return
			this.signingLater = true
			try {
				await this.signContractPayLater(this.parsedIds)
				this.status = 'success'
			} catch (e) {
				console.error('PaymentResult::onPayLater => ERROR', e)
				this.failureToast('monte.payment_result_pay_later_error')
			} finally {
				this.signingLater = false
			}
		},
		goToContracts() {
			this.$router.push({ name: 'ContractList' })
		},
		goToNewContract() {
			this.$router.push({ name: 'ContractNew' })
		},

		// ─── Yousign signature flow (after payment success) ───

		/**
		 * Initialise le flux de signature Yousign après paiement réussi.
		 * Finalise le contrat et génère le lien de signature.
		 */
		async initYousignFlow() {
			const contractId = this.parsedIds[0]
			try {
				await this.finalizeContract(contractId)
				const url = await this.getContractSignatureLink(contractId)
				this.yousignUrl = url
				const invoices = await this.loadInvoiceContract(contractId)
				this.yousignInvoice = invoices && invoices.length > 0 ? invoices[0] : null
				this.yousignPhase = 'signing'
			} catch (e) {
				console.error('PaymentResult::initYousignFlow => ERROR', e)
				this.failureToast('monte.erreur_signature')
			}
		},
		async onManualCheckSigned() {
			this.checkingSignedManual = true
			try {
				const status = await this.getContractSignatureStatus(this.parsedIds[0])
				if (status && status.signed) {
					this.onSignatureConfirmed()
				} else {
					this.failureToast('monte.signature_yousign_en_attente')
				}
			} catch (e) {
				console.error('PaymentResult::onManualCheckSigned => ERROR', e)
			} finally {
				this.checkingSignedManual = false
			}
		},
		onSignatureConfirmed() {
			this.yousignPhase = 'signed'
			this.status = 'success'
		},
		async onDownloadSignedContract() {
			this.downloadingSignedContract = true
			try {
				await this.printContract(this.parsedIds[0])
			} catch (e) {
				console.error('PaymentResult::onDownloadSignedContract => ERROR', e)
				this.failureToast('monte.print_error')
			} finally {
				this.downloadingSignedContract = false
			}
		},
		/**
		 * Relance le paiement marketplace pour les contrats.
		 */
		async retryPayment() {
			if (this.parsedIds.length === 0) return
			this.retrying = true
			try {
				if (!this.marketplace_type) {
					const mp = await this.getMarketplaceType()
					const mpType = mp && mp.retour && mp.retour.type
					this.marketplace_type = (mpType && mpType !== 'none') ? mpType : null
				}
				const result = await this.getBoutiqueContractUrl(this.parsedIds[0])
				if (result && result.url) {
					this.submitMarketplaceForm(result, 'retry')
				} else {
					this.failureToast('monte.payment_result_retry_error')
					this.retrying = false
				}
			} catch (e) {
				console.error('PaymentResult::retryPayment => ERROR', e)
				this.failureToast('monte.payment_result_retry_error')
				this.retrying = false
			}
		},
		goToInvoices() {
			this.$router.push({ name: 'addressedInvoices' })
		},
		/**
		 * Relance le paiement marketplace pour les factures (duedates ou details).
		 */
		async retryInvoicePayment() {
			if (this.parsedIds.length === 0) return
			this.retrying = true
			try {
				if (!this.marketplace_type) {
					const mp = await this.getMarketplaceType()
					const mpType = mp && mp.retour && mp.retour.type
					this.marketplace_type = (mpType && mpType !== 'none') ? mpType : null
				}
				let result = null
				if (this.paymentType === 'duedates') {
					result = await this.redirectBoutique(this.parsedIds)
				} else {
					result = await this.redirectBoutiqueDetails(this.parsedIds, 0)
				}
				const data = result && result.retour ? result.retour : result
				if (data && data.url) {
					this.submitMarketplaceForm(data, 'retry')
				} else {
					this.failureToast('monte.payment_result_retry_error')
					this.retrying = false
				}
			} catch (e) {
				console.error('PaymentResult::retryInvoicePayment => ERROR', e)
				this.failureToast('monte.payment_result_retry_error')
				this.retrying = false
			}
		},
		/**
		 * Soumet le formulaire de paiement adapté au type de marketplace.
		 * @param {Object} data - Données retournées par l'API (url + champs).
		 * @param {string} suffix - Suffixe pour les IDs de boutons.
		 */
		submitMarketplaceForm(data, suffix) {
			if (this.marketplace_type === 'paybox') {
				this.paybox_infos = data
				this.go_paybox = true
				this.$nextTick(() => {
					document.querySelector("#button_go_paybox_" + suffix).click()
				})
			} else if (this.marketplace_type === 'sogecommerce') {
				this.sogecommerce_infos = data
				this.go_sogecommerce = true
				this.$nextTick(() => {
					document.querySelector("#button_go_sogecommerce_" + suffix).click()
				})
			} else {
				window.location.href = data.url
			}
		},
		/**
		 * Génère le style CSS d'un confetti pour l'animation.
		 * @param {number} n - Index du confetti.
		 * @returns {Object} Style CSS inline.
		 */
		confettiStyle(n) {
			const colors = ['#ff6b6b', '#feca57', '#48dbfb', '#1dd1a1', '#ff9ff3', '#54a0ff', '#5f27cd']
			const color = colors[n % colors.length]
			const left = Math.random() * 100
			const delay = Math.random() * 3
			const duration = 4 + Math.random() * 4
			const size = 6 + Math.random() * 6
			const rotation = Math.random() * 360
			return {
				left: `${left}%`,
				backgroundColor: color,
				animationDelay: `${delay}s`,
				animationDuration: `${duration}s`,
				width: `${size}px`,
				height: `${size}px`,
				transform: `rotate(${rotation}deg)`,
			}
		},
	},
}
</script>
